/**
 * Enhanced Game Session Hook
 * Uses PeerJS for BOTH voice chat AND game state sync (via data channels)
 * Database polling as fallback when PeerJS is not connected
 */

import { useCallback, useRef, useEffect, useState } from 'react'
import { usePeerJS, type PeerMessage } from './usePeerJS'
import { useGameSession } from './useGameSession'

interface UseEnhancedGameSessionProps {
  sessionCode?: string
  playerNumber?: 1 | 2
  playerName?: string
}

export function useEnhancedGameSession({
  sessionCode,
  playerNumber,
  playerName,
}: UseEnhancedGameSessionProps) {
  const messageHandlers = useRef<Map<string, (data: any) => void>>(new Map())
  const needsResyncRef = useRef(false)
  const lastSyncTime = useRef<number>(Date.now())
  const [peerJSConnected, setPeerJSConnected] = useState(false)
  const [sessionStatus, setSessionStatus] = useState<'waiting' | 'active' | 'finished'>('waiting')

  // Smart polling: Enable ONLY when:
  // 1. Waiting for Player 2 to join (session.status === 'waiting'), OR
  // 2. PeerJS is disconnected (fallback mode)
  // Once both players are in and PeerJS is connected, polling is DISABLED
  const shouldEnablePolling = !peerJSConnected || sessionStatus === 'waiting'

  // Database polling session (fallback for game state)
  const pollingSession = useGameSession(sessionCode, { enablePolling: shouldEnablePolling })

  // CRITICAL FIX: Memoize callbacks to prevent infinite re-renders in usePeerJS
  // These must be useCallback to avoid recreating on every render
  const handlePeerMessage = useCallback((message: PeerMessage) => {
    // Route PeerJS messages to registered handlers
    const handler = messageHandlers.current.get(message.type)
    if (handler) {
      handler(message.data)
    }

    // Track last sync time for reconnection
    lastSyncTime.current = Date.now()
  }, [])

  // Store fetchSession and sessionCode in refs to avoid dependency issues
  const fetchSessionRef = useRef(pollingSession.fetchSession)
  const sessionCodeRef = useRef(sessionCode)

  useEffect(() => {
    fetchSessionRef.current = pollingSession.fetchSession
    sessionCodeRef.current = sessionCode
  }, [pollingSession.fetchSession, sessionCode])

  // CRITICAL: This callback must have NO dependencies to avoid infinite loops
  const handleConnectionChange = useCallback((status: any, playerId: number) => {
    console.log(`[EnhancedSession] PeerJS connection changed: ${status} (player ${playerId})`)

    // Track PeerJS connection state for smart polling
    const isNowConnected = status === 'connected'
    setPeerJSConnected(isNowConnected)

    if (isNowConnected) {
      console.log('[EnhancedSession] ✅ PeerJS connected - database polling will be DISABLED')
    } else {
      console.log('[EnhancedSession] ⚠️ PeerJS disconnected - database polling will be RE-ENABLED')
    }

    // When reconnecting, request state resync
    if (status === 'connected' && needsResyncRef.current) {
      console.log('[EnhancedSession] Reconnected - requesting state resync')
      // Trigger resync by fetching latest session state
      if (sessionCodeRef.current) {
        fetchSessionRef.current(sessionCodeRef.current)
      }
      needsResyncRef.current = false
    }

    // Mark for resync when disconnected
    if (status === 'disconnected' || status === 'failed') {
      needsResyncRef.current = true
    }
  }, [])

  // PeerJS for BOTH voice chat AND game state sync (data channels)
  const peerjs = usePeerJS({
    sessionCode: sessionCode || '',
    playerNumber: playerNumber || 1,
    playerName: playerName || 'Player',
    enabled: !!sessionCode && !!playerNumber, // Only enable when we have session info
    autoConnectData: true, // Auto-connect data channel (critical for game state sync)
    onMessage: handlePeerMessage,
    onConnectionChange: handleConnectionChange,
  })

  // Register a message handler (for compatibility with game components)
  const onMessage = useCallback((type: string, handler: (data: any) => void) => {
    messageHandlers.current.set(type, handler)
    return () => {
      messageHandlers.current.delete(type)
    }
  }, [])

  // Send game state update (PeerJS primary, database fallback)
  const updateGameState = useCallback(
    async (gameState: any, moveData?: any) => {
      if (!sessionCode || !playerNumber) return false

      const data = moveData || { type: 'state_update' }

      // Try PeerJS first (fast path <100ms)
      if (peerjs.isConnected) {
        const sent = peerjs.sendMessage('game_state', { moveData: data, gameState })
        if (sent) {
          console.log('[EnhancedSession] State sent via PeerJS (<100ms)')
          return true
        }
        console.warn('[EnhancedSession] PeerJS send failed, falling back to database')
      }

      // Fallback to database (slow but reliable ~2-5s)
      console.log('[EnhancedSession] Sending state via database polling')
      return await pollingSession.submitMove(sessionCode, playerNumber, data, gameState)
    },
    [sessionCode, playerNumber, pollingSession, peerjs]
  )

  // Send a move (with optional game state update)
  const sendMove = useCallback(
    async (moveData: any, gameState?: any) => {
      if (!sessionCode || !playerNumber) return false

      // Try PeerJS first (fast path <100ms)
      if (peerjs.isConnected) {
        const sent = peerjs.sendMessage('move', { moveData, gameState })
        if (sent) {
          console.log('[EnhancedSession] Move sent via PeerJS (<100ms)')

          // For critical moves (wins, game_state), also send via database for redundancy
          if (moveData.type === 'win' || moveData.type === 'game_state') {
            console.log('[EnhancedSession] Critical move - also saving to database')
            await pollingSession.submitMove(sessionCode, playerNumber, moveData, gameState)
          }

          return true
        }
        console.warn('[EnhancedSession] PeerJS send failed, falling back to database')
      }

      // Fallback to database (slow but reliable ~2-5s)
      console.log('[EnhancedSession] Sending move via database polling')
      return await pollingSession.submitMove(sessionCode, playerNumber, moveData, gameState)
    },
    [sessionCode, playerNumber, pollingSession, peerjs]
  )

  // Send chat message (PeerJS primary, database fallback)
  const sendChatMessage = useCallback(
    async (message: string) => {
      if (!sessionCode || !playerNumber || !playerName) return false

      // Try PeerJS first (instant delivery)
      if (peerjs.isConnected) {
        const sent = peerjs.sendMessage('chat', {
          player_number: playerNumber,
          player_name: playerName,
          message,
          created_at: new Date().toISOString()
        })
        if (sent) {
          console.log('[EnhancedSession] Chat sent via PeerJS (instant)')

          // Also save to database for message history
          await pollingSession.sendChatMessage(sessionCode, playerNumber, playerName, message)
          return true
        }
      }

      // Fallback to database only
      console.log('[EnhancedSession] Sending chat via database polling')
      return await pollingSession.sendChatMessage(sessionCode, playerNumber, playerName, message)
    },
    [sessionCode, playerNumber, playerName, pollingSession, peerjs]
  )

  // Send heartbeat every 15 seconds to mark player as connected
  useEffect(() => {
    if (!sessionCode || !playerNumber) return

    // Send initial heartbeat immediately
    pollingSession.sendHeartbeat(sessionCode, playerNumber)

    // Send heartbeat every 15 seconds
    const interval = setInterval(() => {
      pollingSession.sendHeartbeat(sessionCode, playerNumber)
    }, 15000)

    return () => {
      clearInterval(interval)
    }
  }, [sessionCode, playerNumber, pollingSession])

  // Update session status when it changes
  useEffect(() => {
    if (pollingSession.session?.status) {
      setSessionStatus(pollingSession.session.status)
    }
  }, [pollingSession.session?.status])

  // NOTE: PeerJS data connection is auto-established by usePeerJS (autoConnectData: true)
  // No need to manually trigger connection here - it happens automatically for Player 2
  // Voice chat connection (with mic) should ONLY be triggered by user clicking "Enable Voice Chat" button

  // Log polling state changes for debugging
  useEffect(() => {
    console.log('[EnhancedSession] 📊 Polling State:', {
      shouldEnablePolling,
      peerJSConnected,
      sessionStatus,
    })
  }, [shouldEnablePolling, peerJSConnected, sessionStatus])

  // Trigger message handlers when new moves arrive
  useEffect(() => {
    const newMoves = pollingSession.moves
    if (!newMoves || newMoves.length === 0) return

    // Process each move and call the appropriate handler
    newMoves.forEach((move) => {
      const moveData = move.move_data

      // Call the handler for this move type if registered
      if (moveData.type) {
        const handler = messageHandlers.current.get(moveData.type)
        if (handler) {
          handler(moveData)
        }
      }

      // Also trigger generic 'move' handler
      const moveHandler = messageHandlers.current.get('move')
      if (moveHandler) {
        moveHandler(moveData)
      }
    })
  }, [pollingSession.moves])

  return {
    // Session management
    session: pollingSession.session,
    gameState: pollingSession.session?.game_state,
    loading: pollingSession.loading,
    error: pollingSession.error || peerjs.error,
    createSession: pollingSession.createSession,
    joinSession: pollingSession.joinSession,
    rejoinSession: pollingSession.rejoinSession,
    fetchSession: pollingSession.fetchSession,

    // Game actions
    updateGameState,
    sendMove,
    sendChatMessage,

    // Voice chat (PeerJS - for backward compatibility)
    voice: {
      connectionStatus: peerjs.connectionStatus,
      voiceStatus: peerjs.voiceStatus,
      isConnected: peerjs.isConnected,
      isMuted: peerjs.isMuted,
      error: peerjs.error,
      connect: peerjs.connect,
      disconnect: peerjs.disconnect,
      toggleMute: peerjs.toggleMute,
    },

    // WebRTC data channel status (for backward compatibility - same as voice now)
    webrtc: {
      connectionStatus: peerjs.connectionStatus,
      isConnected: peerjs.isConnected,
      connect: peerjs.connect,
      disconnect: peerjs.disconnect,
      error: peerjs.error,
    },

    // Real-time messaging (for compatibility)
    onMessage,

    // Status
    moves: pollingSession.moves,
    chatMessages: pollingSession.chatMessages,

    // Connection quality
    isRealtime: peerjs.isConnected,
    latencyEstimate: peerjs.isConnected ? '<100ms' : '2-5s',
  }
}
